// EmailDropZone component
// Reusable drag & drop zone for email files (.eml, .msg)

import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { EnvelopeIcon, CloudArrowUpIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../hooks/useTranslation'

type TargetType = 'account' | 'lead' | 'opportunity' | 'auto'

type EmailDropZoneProps = {
  targetType?: TargetType
  targetId?: string
  onSuccess?: (email: UploadedEmail) => void
  onError?: (error: string) => void
  compact?: boolean
}

type UploadedEmail = {
  id: string
  subject: string
  fromEmail: string
  fromName?: string
  sentAt?: string
  hasAttachments: boolean
  matchedAccount?: { id: string; name: string; confidence?: number } | null
  matchedContact?: { id: string; name: string } | null
  matchedLead?: { id: string; name: string } | null
}

type UploadResponse = {
  success: boolean
  email: UploadedEmail
  requiresConfirmation: boolean
  allMatches: Array<{
    accountId?: string
    accountName?: string
    contactId?: string
    contactName?: string
    leadId?: string
    leadName?: string
    confidence: number
    matchedBy: string
  }>
}

async function uploadEmail(
  file: File,
  targetType?: TargetType,
  targetId?: string
): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (targetType) formData.append('targetType', targetType)
  if (targetId) formData.append('targetId', targetId)

  const response = await fetch('/api/emails/upload', {
    method: 'POST',
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to upload email')
  }

  return response.json()
}

export function EmailDropZone({
  targetType,
  targetId,
  onSuccess,
  onError,
  compact = false,
}: EmailDropZoneProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isDragging, setIsDragging] = useState(false)
  const [uploadedEmail, setUploadedEmail] = useState<UploadedEmail | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadEmail(file, targetType, targetId),
    onSuccess: (data) => {
      setUploadedEmail(data.email)
      setUploadError(null)
      queryClient.invalidateQueries({ queryKey: ['emails'] })
      onSuccess?.(data.email)

      // Clear success message after 5 seconds
      setTimeout(() => setUploadedEmail(null), 5000)
    },
    onError: (error: Error) => {
      setUploadError(error.message)
      setUploadedEmail(null)
      onError?.(error.message)

      // Clear error message after 5 seconds
      setTimeout(() => setUploadError(null), 5000)
    },
  })

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      const emailFile = files.find(
        (f) => f.name.toLowerCase().endsWith('.eml') || f.name.toLowerCase().endsWith('.msg')
      )

      if (!emailFile) {
        setUploadError('Please drop an .eml or .msg file')
        setTimeout(() => setUploadError(null), 5000)
        return
      }

      uploadMutation.mutate(emailFile)
    },
    [uploadMutation]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!file.name.toLowerCase().endsWith('.eml') && !file.name.toLowerCase().endsWith('.msg')) {
        setUploadError('Please select an .eml or .msg file')
        setTimeout(() => setUploadError(null), 5000)
        return
      }

      uploadMutation.mutate(file)
      e.target.value = '' // Reset input
    },
    [uploadMutation]
  )

  // Compact version (for sidebar or inline use)
  if (compact) {
    return (
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative rounded-lg border-2 border-dashed p-3 text-center transition-colors ${
          isDragging
            ? 'border-brand-500 bg-brand-50'
            : uploadError
            ? 'border-red-300 bg-red-50'
            : uploadedEmail
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          type="file"
          accept=".eml,.msg"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploadMutation.isPending}
        />

        {uploadMutation.isPending ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <CloudArrowUpIcon className="h-5 w-5 animate-pulse" />
            <span>Uploading...</span>
          </div>
        ) : uploadedEmail ? (
          <div className="flex items-center justify-center gap-2 text-sm text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span>Email added</span>
          </div>
        ) : uploadError ? (
          <div className="flex items-center justify-center gap-2 text-sm text-red-600">
            <XCircleIcon className="h-5 w-5" />
            <span>{uploadError}</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <EnvelopeIcon className="h-5 w-5" />
            <span>Drop email or click</span>
          </div>
        )}
      </div>
    )
  }

  // Full version
  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
        isDragging
          ? 'border-brand-500 bg-brand-50'
          : uploadError
          ? 'border-red-300 bg-red-50'
          : uploadedEmail
          ? 'border-green-300 bg-green-50'
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <input
        type="file"
        accept=".eml,.msg"
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        disabled={uploadMutation.isPending}
      />

      {uploadMutation.isPending ? (
        <div className="space-y-2">
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-brand-500 animate-pulse" />
          <p className="text-sm font-medium text-gray-900">Uploading email...</p>
          <p className="text-xs text-gray-500">Processing and matching to contacts</p>
        </div>
      ) : uploadedEmail ? (
        <div className="space-y-2">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
          <p className="text-sm font-medium text-gray-900">Email added successfully</p>
          <p className="text-xs text-gray-500">{uploadedEmail.subject}</p>
          {uploadedEmail.matchedAccount && (
            <p className="text-xs text-green-600">
              Linked to {uploadedEmail.matchedAccount.name}
            </p>
          )}
        </div>
      ) : uploadError ? (
        <div className="space-y-2">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
          <p className="text-sm font-medium text-red-900">{uploadError}</p>
          <p className="text-xs text-gray-500">Try again with an .eml or .msg file</p>
        </div>
      ) : (
        <div className="space-y-2">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {isDragging ? 'Drop email file here' : 'Drag and drop email'}
            </p>
            <p className="text-xs text-gray-500">
              or <span className="text-brand-600 hover:underline">click to browse</span>
            </p>
          </div>
          <p className="text-xs text-gray-400">Supports .eml and .msg files</p>
        </div>
      )}
    </div>
  )
}
